import React from "react";
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const tableTheme = createTheme({
    palette: {
        primary: { main: '#ff4400' },
        background: { default: '#ffffff', paper: '#ffffff' },
    },
    typography: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontSize: 13,
    },
    components: {
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-root': {
                        backgroundColor: '#f8fafc',
                        color: '#64748b',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e2e8f0',
                        padding: '12px 16px',
                    },
                },
            },
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    '& .MuiTableRow-root': {
                        transition: 'background-color 0.15s ease',
                        '&:hover': { backgroundColor: '#fff8f6' },
                    },
                    '& .MuiTableCell-root': {
                        fontSize: '0.875rem',
                        color: '#1e293b',
                        borderBottom: '1px solid #f1f5f9',
                        padding: '10px 16px',
                    },
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    fontSize: '0.8125rem',
                    color: '#64748b',
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    padding: '12px 16px !important',
                    borderBottom: '1px solid #f1f5f9',
                    backgroundColor: '#ffffff',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    fontFamily: "'Inter', sans-serif",
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '6px',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ff4400',
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: { color: '#64748b', '&:hover': { color: '#ff4400', backgroundColor: '#fff2ee' } },
            },
        },
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        backgroundColor: '#ff4400 !important',
                        color: '#fff',
                    },
                },
            },
        },
    },
});

const MaterialReactTableWithSearch = ({
    columns, data, enableRowActions, renderRowActions,
    enableRowSelection, renderTopToolbarCustomActions, enableSelectAll
}) => {
    const table = useMaterialReactTable({
        columns,
        data,
        enableSelectAll,
        renderTopToolbarCustomActions,
        enableRowSelection,
        enableStickyHeader: false,
        enableColumnFilterModes: false,
        enableColumnOrdering: false,
        enableGrouping: false,
        enableColumnPinning: false,
        enableFacetedValues: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableColumnActions: false,
        enableRowActions,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        enableGlobalFilter: true,
        muiTableContainerProps: { sx: { maxHeight: 'auto' } },
        initialState: {
            showGlobalFilter: false,
            columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'], right: ['mrt-row-actions'] },
        },
        renderRowActions,
        paginationDisplayMode: 'pages',
        positionToolbarAlertBanner: 'bottom',
        muiSearchTextFieldProps: { size: 'small', variant: 'outlined', placeholder: 'Search…' },
        muiPaginationProps: {
            color: 'primary',
            rowsPerPageOptions: [10, 50, 100, 200],
            shape: 'rounded',
            variant: 'outlined',
        },
    });

    return (
        <ThemeProvider theme={tableTheme}>
            <MaterialReactTable table={table} />
        </ThemeProvider>
    );
};

export default MaterialReactTableWithSearch;
